import React, { useState, useEffect } from 'react';
import './App.css';
import { LanguageProvider } from './LanguageContext';
import LandingPage from './components/LandingPage';
import Login from './components/Login';
import SongList from './components/SongList';
import Game from './components/Game';
import Results from './components/Results';
import Leaderboard from './components/Leaderboard';
import ArtistDashboard from './components/ArtistDashboard';
import Profile from './components/Profile';
import { Song, User, GameResult } from './types';

type View = 'landing' | 'login-player' | 'login-artist' | 'list' | 'game' | 'results' | 'leaderboard' | 'artist-dashboard' | 'profile';

function AppContent() {
  const [view, setView] = useState<View>('landing');
  const [selectedSong, setSelectedSong] = useState<Song | null>(null);
  const [lastResult, setLastResult] = useState<GameResult | null>(null);
  const [authToken, setAuthToken] = useState<string | null>(null);
  const [user, setUser] = useState<User | null>(null);

  useEffect(() => {
    const storedToken = localStorage.getItem('authToken');
    const storedUser = localStorage.getItem('user');
    
    if (storedToken && storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        setAuthToken(storedToken);
        setUser(parsedUser);
        
        if (parsedUser.role === 'artist') {
          setView('artist-dashboard');
        } else {
          setView('list');
        }
      } catch (e) {
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
      }
    }
  }, []);

  const handleSelectSong = (song: Song) => {
    setSelectedSong(song);
    setView('game');
  };

  const handleGameEnd = (result: GameResult) => {
    setLastResult(result);
    setView('results');
  };

  const handleBack = () => {
    setView('list');
    setSelectedSong(null);
  };

  const handleLoginSuccess = (token: string, loggedInUser: User) => {
    setAuthToken(token);
    setUser(loggedInUser);
    
    if (loggedInUser.role === 'artist') {
      setView('artist-dashboard');
    } else {
      setView('list');
    }
  };

  const handleLogout = () => {
    if (authToken) {
      fetch('/api/auth/logout', {
        method: 'POST',
        headers: { Authorization: `Bearer ${authToken}` }
      }).catch(console.error);
    }
    
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    setAuthToken(null);
    setUser(null);
    setView('landing');
  };

  const handlePlayAgain = () => {
    if (selectedSong) {
      setView('game');
    } else {
      setView('list');
    }
  };

  const handleProfileUpdate = (updatedUser: User) => {
    setUser(updatedUser);
    localStorage.setItem('user', JSON.stringify(updatedUser));
  };

  return (
    <div className="app">
      <div className="app-container">
        {view === 'landing' && (
          <LandingPage 
            onGetStarted={() => setView('login-player')}
            onArtistLogin={() => setView('login-artist')}
          />
        )}

        {view === 'login-player' && (
          <Login 
            role="player"
            onBack={() => setView('landing')}
            onLoginSuccess={handleLoginSuccess}
          />
        )}

        {view === 'login-artist' && (
          <Login 
            role="artist"
            onBack={() => setView('landing')}
            onLoginSuccess={handleLoginSuccess}
          />
        )}

        {view === 'list' && (
          <div className="view-transition-enter">
            <header className="app-header">
              <div className="header-user">
                <button 
                  className="avatar-button"
                  onClick={() => setView('profile')}
                >
                  {user?.avatarUrl ? (
                    <img src={user.avatarUrl} alt="" className="header-avatar" />
                  ) : (
                    <span className="header-avatar-placeholder">
                      {(user?.nickname || user?.email || '?')[0].toUpperCase()}
                    </span>
                  )}
                </button>
                <span className="user-name">{user?.nickname || user?.email?.split('@')[0]}</span>
                <button className="btn btn-outline btn-sm" onClick={handleLogout}>
                  ✕
                </button>
              </div>
            </header>
            <SongList onSelectSong={handleSelectSong} />
          </div>
        )}

        {view === 'game' && selectedSong && (
          <div className="view-transition-enter">
            <Game 
              song={selectedSong} 
              onGameEnd={handleGameEnd} 
              onBack={handleBack}
              authToken={authToken || undefined}
            />
          </div>
        )}

        {view === 'results' && lastResult && (
          <div className="view-transition-enter">
            <Results
              result={lastResult}
              onPlayAgain={handlePlayAgain}
              onViewLeaderboard={() => setView('leaderboard')}
              onBackToSongs={handleBack}
            />
          </div>
        )}

        {view === 'leaderboard' && (
          <div className="view-transition-enter">
            <Leaderboard 
              songId={selectedSong?.id}
              songTitle={selectedSong?.title}
              onBack={handleBack}
            />
          </div>
        )}

        {view === 'profile' && user && authToken && (
          <Profile
            user={user}
            authToken={authToken}
            onUpdate={handleProfileUpdate}
            onBack={() => setView('list')}
          />
        )}

        {view === 'artist-dashboard' && user && authToken && (
          <ArtistDashboard 
            authToken={authToken}
            user={user}
            onLogout={handleLogout}
            onProfile={() => setView('profile')}
          />
        )}
      </div>
    </div>
  );
}

function App() {
  return (
    <LanguageProvider>
      <AppContent />
    </LanguageProvider>
  );
}

export default App;
